import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { VideoPlayer } from "./HelpDialogItems";

const FunctionsSwitcher = ({ functions }) => {
  const [selectedFunction, setSelectedFunction] = useState(0);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!functions || functions.length === 0) {
    return <Typography>No functions available</Typography>;
  }

  const handleFunctionClick = (index) => {
    setSelectedFunction(index);
  };

  // Desktop view (large screens)
  if (isDesktop) {
    return (
      <Grid container spacing={5}>
        {/* Functions List - Left Side */}
        <Grid item xs={12} md={5}>
          {functions.map((func, index) => (
            <Box
              key={index}
              component={motion.div}
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2 }}
              onClick={() => handleFunctionClick(index)}
              sx={{
                mb: 0.5,
                py: 2,
                px: 3,
                cursor: "pointer",
                borderRadius: "8px",
                backgroundColor:
                  selectedFunction === index
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    selectedFunction === index
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Typography
                variant="h6"
                fontWeight={selectedFunction === index ? 700 : 600}
                sx={{ fontSize: "1.05rem" }}
                gutterBottom
                color={
                  selectedFunction === index ? "text.primary" : "text.primary"
                }
              >
                {func.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  opacity: selectedFunction === index ? 1 : 0.85,
                  fontSize: "1rem",
                }}
              >
                {func.subtitle || func.description}
              </Typography>
            </Box>
          ))}
        </Grid>

        {/* Video Display - Right Side */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                width: "100%",
              }}
            >
              <VideoPlayer
                key={functions[selectedFunction].videoUrl}
                videoUrl={functions[selectedFunction].videoUrl}
                poster={functions[selectedFunction].poster}
                autoplayOnScroll={true}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  }

  // Mobile and Tablet View (stacked layout)
  return (
    <Box sx={{ width: "100%" }}>
      {/* Video Container - Top */}
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          mb: 2,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <VideoPlayer
            key={functions[selectedFunction].videoUrl}
            videoUrl={functions[selectedFunction].videoUrl}
            poster={functions[selectedFunction].poster}
            autoplayOnScroll={true}
          />
        </Box>
      </Box>

      {/* Horizontally scrollable titles below video */}
      <Box
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 5, // Ensure this is above the video
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            width: "100%",
            pb: 1,
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: "10px",
            },
          }}
        >
          {functions.map((func, index) => (
            <Box
              key={index}
              onClick={() => handleFunctionClick(index)}
              sx={{
                minWidth: "140px",
                maxWidth: "300px",
                flexShrink: 0,
                mr: 1.5,
                p: 1.5,
                cursor: "pointer",
                borderRadius: "6px",
                backgroundColor:
                  selectedFunction === index
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.background.paper, 0.7),
                // border: `1px solid ${
                //   selectedFunction === index
                //     ? alpha(theme.palette.primary.main, 0.2)
                //     : alpha(theme.palette.divider, 0.2)
                // }`,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={selectedFunction === index ? 600 : 500}
                color={
                  selectedFunction === index ? "primary.main" : "text.primary"
                }
              >
                {func.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.85rem",
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {func.subtitle || func.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FunctionsSwitcher;
